import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Link2, Copy, Trash2, Eye, AlertCircle, CheckCircle2,
         Mail, Users } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api, formatApiError } from "@/lib/api";
import { handleApiError } from "@/lib/errors";

/**
 * ShareScorecardDialog — modal for minting + revoking public scorecard links.
 *
 * Loaded lazily (open=false on mount) so we don't bother the API on every
 * scan-detail page render — only when the user actually clicks "Share".
 */
const EXPIRY_OPTIONS = [
  { value: "1d",    label: "24 hours" },
  { value: "7d",    label: "7 days", recommended: true },
  { value: "30d",   label: "30 days" },
  { value: "never", label: "Never expires" },
];

export default function ShareScorecardDialog({ open, onOpenChange, scanId, documentName }) {
  const navigate = useNavigate();
  const [shares, setShares] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [expiry, setExpiry] = useState("7d");
  const [label, setLabel] = useState("");
  const [requireEmail, setRequireEmail] = useState(false);

  useEffect(() => {
    if (!open || !scanId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/scans/${scanId}/scorecard/shares`);
        if (!cancelled) setShares(data);
      } catch (err) {
        if (!cancelled) toast.error("Couldn't load share links", { description: formatApiError(err) });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, scanId]);

  const createShare = async () => {
    setCreating(true);
    try {
      const { data } = await api.post(`/scans/${scanId}/scorecard/shares`, {
        expiry, label: label.trim() || null, require_email: requireEmail,
      });
      setShares((prev) => [data, ...prev]);
      setLabel("");
      setExpiry("7d");
      setRequireEmail(false);
      // Copy fresh link to clipboard immediately — 90% of users want this.
      try {
        await navigator.clipboard.writeText(data.url);
        toast.success("Link created and copied to clipboard", {
          description: data.expires_at
            ? `Expires ${new Date(data.expires_at).toLocaleString()}`
            : "Never expires — revoke when done.",
        });
      } catch {
        toast.success("Link created", { description: data.url });
      }
    } catch (err) {
      handleApiError(err, "Couldn't create link", navigate);
    } finally {
      setCreating(false);
    }
  };

  const revokeShare = async (token) => {
    if (!window.confirm("Revoke this share link? Anyone who has it will lose access immediately.")) {
      return;
    }
    try {
      await api.delete(`/scorecard/shares/${token}`);
      setShares((prev) => prev.map((s) =>
        s.token === token ? { ...s, revoked_at: new Date().toISOString(), is_active: false } : s
      ));
      toast.success("Link revoked");
    } catch (err) {
      toast.error("Couldn't revoke link", { description: formatApiError(err) });
    }
  };

  const copy = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied to clipboard");
    } catch {
      toast.error("Couldn't copy", { description: url });
    }
  };

  const activeShares = shares.filter((s) => s.is_active);
  const inactiveShares = shares.filter((s) => !s.is_active);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl" data-testid="share-scorecard-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Link2 className="h-5 w-5 text-blue-600" />
            Share scorecard via link
          </DialogTitle>
          <DialogDescription>
            Anyone with the link can view the one-page Compliance Scorecard PDF — no AgentFlow account needed.
            {documentName ? <span className="font-medium text-slate-900"> · {documentName}</span> : null}
          </DialogDescription>
        </DialogHeader>

        {/* Create form ---------------------------------------------------- */}
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="sm:col-span-2">
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                Label <span className="text-slate-400 font-normal">(optional)</span>
              </label>
              <input
                type="text"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="e.g. Outside counsel — Goodwin"
                maxLength={80}
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600/40 focus:border-blue-500"
                data-testid="share-label-input"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">Expires</label>
              <select
                value={expiry}
                onChange={(e) => setExpiry(e.target.value)}
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-600/40 focus:border-blue-500"
                data-testid="share-expiry-select"
              >
                {EXPIRY_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}{o.recommended ? " (recommended)" : ""}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <Button
            onClick={createShare}
            disabled={creating}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white h-10"
            data-testid="share-create-btn"
          >
            {creating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Link2 className="h-4 w-4 mr-2" />}
            Create share link
          </Button>
          <label className="flex items-start gap-2.5 cursor-pointer select-none mt-1">
            <input
              type="checkbox"
              checked={requireEmail}
              onChange={(e) => setRequireEmail(e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
              data-testid="share-require-email-toggle"
            />
            <span className="text-xs leading-tight">
              <span className="font-medium text-slate-900 inline-flex items-center gap-1">
                <Mail className="h-3 w-3" /> Require email to view
              </span>
              <span className="block text-slate-500 mt-0.5">
                Recipients enter their email before seeing the PDF. You'll see who opened the link.
              </span>
            </span>
          </label>
        </div>

        {/* Existing shares ----------------------------------------------- */}
        <div className="space-y-3 max-h-72 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center text-sm text-slate-500 py-6 gap-2">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading links…
            </div>
          ) : shares.length === 0 ? (
            <div className="text-center py-6 text-sm text-slate-500" data-testid="share-empty">
              No share links yet. Create one above.
            </div>
          ) : (
            <>
              {activeShares.length > 0 && (
                <ShareList rows={activeShares} onCopy={copy} onRevoke={revokeShare} />
              )}
              {inactiveShares.length > 0 && (
                <>
                  <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-400 pt-2 border-t border-slate-100">
                    Revoked / expired
                  </div>
                  <ShareList rows={inactiveShares} onCopy={copy} onRevoke={revokeShare} muted />
                </>
              )}
            </>
          )}
        </div>

        <DialogFooter className="text-[11px] text-slate-500 sm:justify-start gap-1">
          <AlertCircle className="h-3.5 w-3.5 inline shrink-0" />
          Confidential — share only with parties bound to confidentiality (counsel, investors, bankers).
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ViewersList({ viewers }) {
  const [expanded, setExpanded] = React.useState(false);
  const visible = expanded ? viewers : viewers.slice(0, 3);
  return (
    <div className="mt-2 rounded-lg bg-slate-50 border border-slate-200 px-3 py-2" data-testid="share-viewers-list">
      <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 inline-flex items-center gap-1">
        <Users className="h-3 w-3" /> Opened by ({viewers.length})
      </div>
      <ul className="mt-1.5 space-y-1">
        {visible.map((v) => (
          <li key={v.email} className="flex items-center justify-between text-xs">
            <span className="text-slate-700 font-medium truncate">{v.email}</span>
            <span className="text-slate-500 tabular-nums shrink-0 ml-2">
              {v.view_count} view{v.view_count === 1 ? "" : "s"}
              {v.last_viewed_at ? ` · ${new Date(v.last_viewed_at).toLocaleDateString()}` : ""}
            </span>
          </li>
        ))}
      </ul>
      {viewers.length > 3 && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="mt-1 text-[11px] font-semibold text-blue-700 hover:text-blue-800"
        >
          {expanded ? "Show less" : `Show ${viewers.length - 3} more`}
        </button>
      )}
    </div>
  );
}

function ShareList({ rows, onCopy, onRevoke, muted = false }) {
  return (
    <ul className="space-y-2">
      {rows.map((s) => (
        <li
          key={s.token}
          className={`rounded-xl border p-3 ${muted ? "border-slate-200 bg-slate-50 opacity-70" : "border-slate-200 bg-white"}`}
          data-testid={`share-row-${s.token.slice(0, 8)}`}
        >
          <div className="flex items-start gap-3">
            <span className={`inline-flex h-8 w-8 items-center justify-center rounded-lg shrink-0 ${
              s.is_active ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-500"
            }`}>
              {s.is_active ? <CheckCircle2 className="h-4 w-4" /> : <AlertCircle className="h-4 w-4" />}
            </span>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-slate-900 truncate">
                {s.label || "Untitled link"}
              </div>
              <div className="text-xs text-slate-500 mt-0.5 truncate font-mono">
                {s.url}
              </div>
              <div className="flex flex-wrap items-center gap-3 mt-2 text-[11px] text-slate-500">
                {s.require_email ? (
                  <span className="inline-flex items-center gap-1 rounded-full bg-blue-50 text-blue-700 px-2 py-0.5 font-semibold">
                    <Mail className="h-3 w-3" /> Email gated
                  </span>
                ) : null}
                <span className="inline-flex items-center gap-1">
                  <Eye className="h-3 w-3" /> {s.view_count} view{s.view_count === 1 ? "" : "s"}
                </span>
                {s.last_viewed_at && (
                  <span>Last viewed {new Date(s.last_viewed_at).toLocaleString()}</span>
                )}
                {s.is_active ? (
                  s.expires_at ? (
                    <span>Expires {new Date(s.expires_at).toLocaleDateString()}</span>
                  ) : (
                    <span>Never expires</span>
                  )
                ) : s.revoked_at ? (
                  <span>Revoked {new Date(s.revoked_at).toLocaleDateString()}</span>
                ) : (
                  <span>Expired</span>
                )}
              </div>
              {/* Viewer list for email-gated shares ---------------------- */}
              {s.require_email && s.viewers?.length > 0 && (
                <ViewersList viewers={s.viewers} />
              )}
            </div>
            {s.is_active ? (
              <div className="flex items-center gap-1.5 shrink-0">
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 px-2.5 border-slate-300"
                  onClick={() => onCopy(s.url)}
                  data-testid={`share-copy-${s.token.slice(0, 8)}`}
                >
                  <Copy className="h-3.5 w-3.5" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 px-2.5 border-rose-200 text-rose-700 hover:bg-rose-50 hover:text-rose-800"
                  onClick={() => onRevoke(s.token)}
                  data-testid={`share-revoke-${s.token.slice(0, 8)}`}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            ) : null}
          </div>
        </li>
      ))}
    </ul>
  );
}
